<template>
    <div class="container-fluid report-page">
        <div class="page-heading mb-4">
            <p class="page-eyebrow">LAPORAN</p>
            <h1 class="h3 mb-1 text-gray-800">Partisipasi sesi</h1>
            <p class="mb-0 text-muted">Pantau kehadiran unik peserta pada setiap sesi event.</p>
        </div>

        <section class="report-filter card mb-4" aria-label="Filter laporan partisipasi sesi">
            <div class="card-body">
                <label for="session-report-event" class="mb-2 font-weight-bold">Event</label>
                <select id="session-report-event" v-model="selectedEventId" class="form-control" :disabled="eventsLoading" @change="loadReport">
                    <option value="">Pilih event</option>
                    <option v-for="event in events" :key="event.id" :value="event.id">{{ event.name }} ({{ formatDate(event.startDate) }})</option>
                </select>
            </div>
        </section>

        <div v-if="loading" class="report-state"><i class="pi pi-spin pi-spinner mr-2" aria-hidden="true"></i>Memuat laporan partisipasi…</div>
        <div v-else-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
        <div v-else-if="!selectedEventId" class="report-state report-state--empty"><i class="pi pi-users" aria-hidden="true"></i><p>Pilih event untuk melihat partisipasi sesinya.</p></div>

        <template v-else-if="report">
            <section class="report-event-summary mb-4">
                <p class="section-eyebrow">RINGKASAN EVENT</p>
                <h2>{{ report.eventName }}</h2>
                <p>{{ formatDate(report.eventStartDate) }} – {{ formatDate(report.eventEndDate) }}</p>
            </section>

            <div class="row report-metrics mb-4">
                <div class="col-xl-3 col-md-6 mb-3"><MetricCard label="Total sesi" :value="report.totalSessionCount" icon="pi-calendar" /></div>
                <div class="col-xl-3 col-md-6 mb-3"><MetricCard label="Peserta unik" :value="report.totalUniqueParticipantCount" icon="pi-users" /></div>
                <div class="col-xl-3 col-md-6 mb-3"><MetricCard label="Total check-in" :value="report.totalCheckInCount" icon="pi-check-circle" /></div>
                <div class="col-xl-3 col-md-6 mb-3"><MetricCard label="Rata-rata per sesi" :value="formatNumber(report.averageParticipantPerSession)" icon="pi-chart-bar" /></div>
            </div>

            <section class="card report-table-card">
                <div class="card-body p-0">
                    <div class="table-responsive">
                        <table class="table mb-0 report-table">
                            <thead>
                            <tr>
                                <th scope="col">Sesi</th>
                                <th scope="col">Waktu &amp; lokasi</th>
                                <th scope="col" class="text-center">Peserta unik</th>
                                <th scope="col" class="text-right">Detail</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="report.sessions.length === 0"><td colspan="4"><div class="table-empty">Belum ada sesi untuk event ini.</div></td></tr>
                            <tr v-for="session in report.sessions" :key="session.sessionId">
                                <td class="font-weight-bold">{{ session.sessionName }}</td>
                                <td><div>{{ formatDateTime(session.startDate) }} – {{ formatTime(session.endDate) }}</div><small class="text-muted">{{ session.locationName || 'Lokasi belum diatur' }}</small></td>
                                <td class="text-center"><span class="participant-count">{{ session.participantCount }}</span></td>
                                <td class="text-right"><button class="btn btn-sm btn-outline-primary" type="button" :disabled="session.attendees.length === 0" @click="showAttendees(session)"><i class="pi pi-users mr-1" aria-hidden="true"></i>{{ session.attendees.length }}</button></td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </template>

        <Dialog v-model:visible="attendeesVisible" modal :header="selectedSession ? selectedSession.sessionName : 'Peserta sesi'" :style="{ width: 'min(760px, 92vw)' }">
            <div v-if="selectedSession" class="table-responsive">
                <table class="table table-sm mb-0 attendee-table">
                    <thead><tr><th>Peserta</th><th>Organisasi</th><th>Waktu scan</th></tr></thead>
                    <tbody><tr v-for="attendee in selectedSession.attendees" :key="attendee.memberId"><td>{{ attendee.memberName }}</td><td>{{ attendee.organizationName || '—' }}</td><td>{{ formatDateTime(attendee.scannedAt) }}</td></tr></tbody>
                </table>
            </div>
        </Dialog>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import Dialog from 'primevue/dialog'
import moment from 'moment'
import apiService from '@/services/ApiService'

const MetricCard = {
    props: { label: String, value: [String, Number], icon: String },
    template: `<div class="card metric-card h-100"><div class="card-body"><div><p>{{ label }}</p><strong>{{ value }}</strong></div><i :class="['pi', icon]" aria-hidden="true"></i></div></div>`
}

export default defineComponent({
    name: 'SessionParticipationReport',
    components: { Dialog, MetricCard },
    data() {
        return {
            events: [], eventsLoading: false, selectedEventId: '', report: null,
            loading: false, error: '', attendeesVisible: false, selectedSession: null
        }
    },
    created() { this.loadEvents() },
    methods: {
        async loadEvents() {
            this.eventsLoading = true
            try {
                const response = await apiService.get('/event', { params: { page: 1, limit: 100 } })
                this.events = response.data.data || []
            } catch (error) {
                this.error = error.response?.data?.Message || 'Gagal memuat daftar event.'
            } finally { this.eventsLoading = false }
        },
        async loadReport() {
            this.report = null
            this.error = ''
            this.selectedSession = null
            if (!this.selectedEventId) return
            this.loading = true
            try {
                const response = await apiService.get('/report/session-participation', { params: { eventId: this.selectedEventId } })
                this.report = response.data
            } catch (error) {
                this.error = error.response?.data?.Message || 'Gagal memuat laporan partisipasi sesi.'
            } finally { this.loading = false }
        },
        showAttendees(session) { this.selectedSession = session; this.attendeesVisible = true },
        formatNumber(value) { return Number(value || 0).toFixed(1) },
        formatDate(value) { return value ? moment(value).format('DD MMM YYYY') : '—' },
        formatDateTime(value) { return value ? moment(value).format('DD MMM YYYY, HH:mm') : '—' },
        formatTime(value) { return value ? moment(value).format('HH:mm') : '—' }
    }
})
</script>

<style scoped>
.page-heading{border-bottom:1px solid #e2e6ea;padding-bottom:1rem}.page-eyebrow,.section-eyebrow{color:#426cc6;font-size:.72rem;font-weight:700;letter-spacing:.08em;margin:0 0 .35rem}.report-filter{border:1px solid #e2e6ec;border-radius:.65rem}.report-filter .form-control{max-width:480px}.report-state{align-items:center;background:#fff;border:1px solid #e2e6ec;border-radius:.65rem;color:#5c6470;display:flex;justify-content:center;min-height:180px}.report-state--empty{flex-direction:column;gap:.75rem}.report-state--empty i{color:#426cc6;font-size:2rem}.report-state--empty p{margin:0}.report-event-summary{background:linear-gradient(120deg,#3656bf,#426cc6);border-radius:.65rem;color:#fff;padding:1.5rem}.report-event-summary .section-eyebrow{color:#dce7ff}.report-event-summary h2{font-size:1.35rem;margin-bottom:.4rem}.report-event-summary p:last-child{margin:0;opacity:.9}.metric-card{border:1px solid #e2e6ec;border-radius:.65rem}.metric-card .card-body{align-items:center;display:flex;justify-content:space-between;padding:1.15rem}.metric-card p{color:#6c757d;font-size:.75rem;font-weight:700;letter-spacing:.06em;margin:0 0 .35rem;text-transform:uppercase}.metric-card strong{color:#2b2f36;font-size:1.45rem}.metric-card .pi{color:#426cc6;font-size:1.75rem}.report-table-card{border:1px solid #e2e6ec;border-radius:.65rem;overflow:hidden}.report-table th{background:#f7f8fa;border-top:0;color:#59616e;font-size:.72rem;letter-spacing:.05em;text-transform:uppercase}.report-table td,.report-table th{padding:1rem;vertical-align:middle}.participant-count{background:#e7f7ed;border-radius:999px;color:#18713a;font-weight:700;padding:.3rem .65rem}.table-empty{color:#6c757d;padding:2rem;text-align:center}.attendee-table th{border-top:0;color:#59616e;font-size:.72rem;text-transform:uppercase}@media(max-width:576px){.report-table td,.report-table th{padding:.75rem}.report-event-summary{padding:1.25rem}}
</style>
